import { motion, AnimatePresence } from 'framer-motion'
import type { AITaskSuggestion } from '../types/task'
import { 
  XMarkIcon,
  SparklesIcon,
  CheckIcon,
  ClockIcon,
  LightBulbIcon,
  FireIcon,
  ArrowUpIcon,
  MinusIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'

interface AITaskSuggestionsProps {
  isOpen: boolean
  onClose: () => void
  suggestions: AITaskSuggestion[]
  onAccept: (suggestion: AITaskSuggestion) => void
  onDismiss: (suggestionId: string) => void
}

const AITaskSuggestions = ({ 
  isOpen, 
  onClose, 
  suggestions, 
  onAccept, 
  onDismiss 
}: AITaskSuggestionsProps) => {
  const getPriorityIcon = (priority: AITaskSuggestion['priority']) => {
    switch (priority) {
      case 'urgent':
        return <FireIcon className="w-4 h-4 text-red-400" />
      case 'high':
        return <ArrowUpIcon className="w-4 h-4 text-orange-400" />
      case 'medium':
        return <MinusIcon className="w-4 h-4 text-yellow-400" />
      case 'low':
        return <MinusIcon className="w-4 h-4 text-gray-400" />
    }
  }

  const getPriorityColor = (priority: AITaskSuggestion['priority']) => {
    switch (priority) {
      case 'urgent':
        return 'border-red-500/30 bg-red-500/10'
      case 'high':
        return 'border-orange-500/30 bg-orange-500/10'
      case 'medium':
        return 'border-yellow-500/30 bg-yellow-500/10'
      case 'low':
        return 'border-gray-500/30 bg-gray-500/10'
    }
  }

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return 'text-green-400'
    if (confidence >= 70) return 'text-yellow-400'
    return 'text-orange-400'
  }

  const getBasedOnIcon = (basedOn: AITaskSuggestion['basedOn']) => {
    switch (basedOn) {
      case 'shoot':
        return '📸'
      case 'deadline':
        return '⏰'
      case 'pattern':
        return '📊'
      case 'client':
        return '👤'
      default:
        return '🤖'
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto glass-mockup rounded-2xl p-6"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30">
                <SparklesIcon className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">AI Task Suggestions</h2>
                <p className="text-gray-400 text-sm">Smart recommendations based on your workflow</p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </motion.button>
          </div>

          {/* Suggestions */}
          {suggestions.length === 0 ? (
            <div className="text-center py-12">
              <LightBulbIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No suggestions available</h3>
              <p className="text-gray-400">
                AI suggestions will appear here based on your activity and patterns
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {suggestions.map((suggestion, index) => (
                <motion.div
                  key={suggestion.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`p-6 rounded-xl border transition-all ${getPriorityColor(suggestion.priority)}`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      {getPriorityIcon(suggestion.priority)}
                      <div>
                        <h3 className="text-white font-semibold">{suggestion.title}</h3>
                        <div className="flex items-center space-x-3 mt-1">
                          <span className="text-xs font-medium text-gray-400 uppercase">
                            {suggestion.priority} Priority
                          </span>
                          <span className="text-xs text-gray-500">•</span>
                          <span className="text-xs text-gray-400 capitalize">
                            {suggestion.category.replace('_', ' ')}
                          </span>
                          {suggestion.estimatedMinutes && (
                            <>
                              <span className="text-xs text-gray-500">•</span>
                              <div className="flex items-center space-x-1">
                                <ClockIcon className="w-3 h-3 text-gray-400" />
                                <span className="text-xs text-gray-400">
                                  {suggestion.estimatedMinutes}m
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => onAccept(suggestion)}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-xl transition-colors border border-green-500/30"
                      >
                        <CheckIcon className="w-4 h-4" />
                        <span className="text-sm font-medium">Accept</span>
                      </motion.button>
                      
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => onDismiss(suggestion.id)}
                        className="p-2 rounded-xl bg-gray-500/20 hover:bg-gray-500/30 text-gray-400 transition-colors"
                        title="Dismiss suggestion"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </motion.button>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-300 mb-4 leading-relaxed">
                    {suggestion.description}
                  </p>

                  {/* AI Reasoning */}
                  <div className="bg-white/5 rounded-xl p-4 mb-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <span className="text-sm">{getBasedOnIcon(suggestion.basedOn)}</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="text-sm font-medium text-purple-400">AI Reasoning</h4>
                          <div className={`flex items-center space-x-1 ${getConfidenceColor(suggestion.confidence)}`}>
                            <span className="text-xs font-medium">
                              {suggestion.confidence}% confidence
                            </span>
                          </div>
                        </div>
                        <p className="text-gray-400 text-sm leading-relaxed">
                          {suggestion.reasoning}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Based On Badge */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs font-medium">
                        Based on {suggestion.basedOn}
                      </span>
                      {suggestion.confidence < 70 && (
                        <div className="flex items-center space-x-1 text-orange-400">
                          <ExclamationTriangleIcon className="w-3 h-3" />
                          <span className="text-xs">Low confidence</span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Footer */}
          {suggestions.length > 0 && (
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 text-gray-400">
                  <SparklesIcon className="w-4 h-4" />
                  <span className="text-sm">
                    {suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''} available
                  </span>
                </div>
                
                <div className="flex space-x-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => suggestions.forEach(s => onDismiss(s.id))}
                    className="px-4 py-2 bg-gray-500/20 hover:bg-gray-500/30 text-gray-400 rounded-xl transition-colors text-sm"
                  >
                    Dismiss All
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onClose}
                    className="px-6 py-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 text-purple-400 rounded-xl transition-all border border-purple-500/30 text-sm"
                  >
                    Close
                  </motion.button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default AITaskSuggestions 